import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Layout, PageHeader } from '../components/Layout';
import { useAuth } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Check, X, ChevronsUpDown, Copy, AlertTriangle } from 'lucide-react';
import { format, parseISO, isBefore, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ReservationTable } from '../components/reservations/ReservationTable';
import { NewClientDialog } from '../components/reservations/NewClientDialog';
import { EventContactsPicker } from '../components/reservations/EventContactsPicker';

const EMPTY_EVENT_CONTACTS = { responsable_id: '', validateur_id: '', payeur_id: '' };
const toEventContactsForm = (ec) => ({
  responsable_id: ec?.responsable_id || '',
  validateur_id: ec?.validateur_id || '',
  payeur_id: ec?.payeur_id || '',
});
const toEventContactsPayload = (ec) => {
  if (!ec || (!ec.responsable_id && !ec.validateur_id && !ec.payeur_id)) return undefined;
  return {
    responsable_id: ec.responsable_id || null,
    validateur_id: ec.validateur_id || null,
    payeur_id: ec.payeur_id || null,
  };
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function ReservationsPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [reservations, setReservations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [clients, setClients] = useState([]);
  const [userClient, setUserClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roomFilter, setRoomFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(searchParams.get('new') === 'true');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  
  // Pagination state
  const [pageSize, setPageSize] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Client search
  const [clientSearchOpen, setClientSearchOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  
  // New client dialog
  const [newClientDialogOpen, setNewClientDialogOpen] = useState(false);
  const emptyNewClient = {
    name: '',
    client_type: 'entreprise',
    convention: false,
    company_name: '',
    siret: '',
    address: '',
    contact: { name: '', email: '', phone: '' },
    notes: '',
  };
  const [newClientData, setNewClientData] = useState(emptyNewClient);
  const [creatingClient, setCreatingClient] = useState(false);
  
  // Dynamic pricing
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  
  // Multi-slot mode
  const [multiSlotMode, setMultiSlotMode] = useState(false);
  const [slots, setSlots] = useState([{ room_id: '', start_datetime: '', end_datetime: '' }]);
  const [slotPrices, setSlotPrices] = useState({});
  
  // Custom pricing (fusionné: type "amount" ou "percentage")
  const [isCustomPrice, setIsCustomPrice] = useState(false);
  const [customPriceType, setCustomPriceType] = useState('amount'); // 'amount' | 'percentage'
  const [customPriceValue, setCustomPriceValue] = useState('');
  
  // Services
  const [selectedServices, setSelectedServices] = useState([]);
  
  // Conflict modal (for duplicate bookings)
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [conflictMessage, setConflictMessage] = useState('');
  
  // Past date warning
  const [pastDateWarning, setPastDateWarning] = useState(false);
  
  const [formData, setFormData] = useState({
    room_id: '',
    client_id: '',
    title: '',
    start_datetime: '',
    end_datetime: '',
    description: '',
    attendees_count: '',
    notes: '',
    special_needs: '',
    services: [],
    event_contacts: EMPTY_EVENT_CONTACTS
  });

  const isAdmin = user?.role === 'admin';
  const isGestionnaire = user?.role === 'gestionnaire';
  const isUser = user?.role === 'utilisateur';

  useEffect(() => {
    loadData();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch user's linked client if they're a regular user
  useEffect(() => {
    if (user && user.role === 'utilisateur') {
      fetchUserClient();
    }
  }, [user]);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUserClient = useCallback(async () => {
    try {
      const response = await api.get('/clients/me');
      if (response.data) {
        setUserClient(response.data);
        setFormData(prev => ({ ...prev, client_id: response.data.id }));
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Votre profil bénéficiaire n\'est pas configuré. Contactez l\'administrateur.');
      }
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [resResponse, roomsResponse, clientsResponse] = await Promise.all([
        api.get('/reservations'),
        api.get('/rooms'),
        api.get('/clients')
      ]);
      setReservations(resResponse.data);
      setRooms(roomsResponse.data);
      setClients(clientsResponse.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  const filteredReservations = reservations.filter(res => {
    if (search) {
      const searchLower = search.toLowerCase();
      if (!res.title.toLowerCase().includes(searchLower) &&
          !res.client_name?.toLowerCase().includes(searchLower) &&
          !res.room_name?.toLowerCase().includes(searchLower)) {
        return false;
      }
    }
    if (statusFilter !== 'all' && res.status !== statusFilter) return false;
    if (roomFilter !== 'all' && res.room_id !== roomFilter) return false;
    return true;
  });

  // Paginated reservations
  const paginatedReservations = useMemo(() => {
    if (pageSize === 'all') return filteredReservations;
    const startIndex = (currentPage - 1) * pageSize;
    return filteredReservations.slice(startIndex, startIndex + pageSize);
  }, [filteredReservations, currentPage, pageSize]);

  const totalPages = useMemo(() => {
    if (pageSize === 'all') return 1;
    return Math.ceil(filteredReservations.length / pageSize);
  }, [filteredReservations.length, pageSize]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, roomFilter, pageSize]);

  // Filter clients based on search
  const filteredClients = useMemo(() => {
    if (!clientSearch) return clients;
    const searchLower = clientSearch.toLowerCase();
    return clients.filter(c => 
      c.name.toLowerCase().includes(searchLower) ||
      c.contact?.email?.toLowerCase().includes(searchLower)
    );
  }, [clients, clientSearch]);

  const selectedClient = clients.find(c => c.id === formData.client_id) || (userClient?.id === formData.client_id ? userClient : null);

  const handleEventContactCreated = (contact) => {
    const addTo = (c) => (c.id === formData.client_id ? { ...c, contacts: [...(c.contacts || []), contact] } : c);
    setClients((prev) => prev.map(addTo));
    setUserClient((prev) => (prev ? addTo(prev) : prev));
  };

  // Create new client
  const handleCreateNewClient = async () => {
    const name = (newClientData.name || '').trim();
    const contactName = (newClientData.contact?.name || '').trim();
    const contactEmail = (newClientData.contact?.email || '').trim();
    const contactPhone = (newClientData.contact?.phone || '').trim();

    if (!name) {
      toast.error('Le nom / prénom du bénéficiaire est obligatoire');
      return;
    }
    if (!contactName) {
      toast.error('Le nom du contact principal est obligatoire');
      return;
    }
    if (!contactEmail) {
      toast.error("L'email du contact est obligatoire");
      return;
    }
    if (!contactPhone) {
      toast.error('Le téléphone du contact est obligatoire');
      return;
    }

    setCreatingClient(true);
    try {
      const response = await api.post('/clients', {
        name,
        client_type: newClientData.client_type,
        convention: !!newClientData.convention,
        company_name: (newClientData.company_name || '').trim(),
        siret: (newClientData.siret || '').trim(),
        address: (newClientData.address || '').trim(),
        contact: {
          name: contactName,
          email: contactEmail,
          phone: contactPhone,
        },
        notes: (newClientData.notes || '').trim(),
      });

      // Add new client to list and select it
      const newClient = response.data;
      setClients((prev) => [...prev, newClient]);
      setFormData((prev) => ({ ...prev, client_id: newClient.id, event_contacts: EMPTY_EVENT_CONTACTS }));

      toast.success(`Bénéficiaire "${newClient.name}" créé avec succès`);
      setNewClientDialogOpen(false);
      setNewClientData(emptyNewClient);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((d) => `${(d.loc || []).slice(-2).join('.')}: ${d.msg}`).join(' | ')
        : detail || 'Erreur lors de la création du bénéficiaire';
      toast.error(message);
    } finally {
      setCreatingClient(false);
    }
  };

  // Fetch price when room and client are selected (single-slot mode)
  useEffect(() => {
    const fetchPrice = async () => {
      if (formData.room_id && formData.client_id && formData.start_datetime && formData.end_datetime) {
        const client = clients.find(c => c.id === formData.client_id);
        if (client) {
          setPriceLoading(true);
          try {
            const response = await api.post('/reservations/estimate-price', {
              slots: [{ room_id: formData.room_id, start_datetime: formData.start_datetime, end_datetime: formData.end_datetime }],
              client_id: formData.client_id
            });
            setEstimatedPrice(response.data);
            if (!isCustomPrice) {
              setCustomPriceValue(response.data.total?.toString() || '0');
            }
          } catch (error) {
            setEstimatedPrice(null);
          } finally {
            setPriceLoading(false);
          }
        }
      } else {
        setEstimatedPrice(null);
      }
    };
    fetchPrice();
  }, [formData.room_id, formData.client_id, formData.start_datetime, formData.end_datetime, clients]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch prices for multi-slot mode
  useEffect(() => {
    const fetchSlotPrices = async () => {
      if (!multiSlotMode || !formData.client_id) {
        setSlotPrices({});
        return;
      }
      
      const validSlots = slots.filter(s => s.room_id && s.start_datetime && s.end_datetime);
      if (validSlots.length === 0) return;
      
      try {
        const response = await api.post('/reservations/estimate-price', {
          slots: validSlots.map(s => ({ room_id: s.room_id, start_datetime: s.start_datetime, end_datetime: s.end_datetime })),
          client_id: formData.client_id
        });
        
        // Map back to slot indices
        const newPrices = {};
        let detailIdx = 0;
        for (let i = 0; i < slots.length; i++) {
          const slot = slots[i];
          if (slot.room_id && slot.start_datetime && slot.end_datetime) {
            const room = rooms.find(r => r.id === slot.room_id);
            const detail = response.data.slot_details?.[detailIdx] || {};
            newPrices[i] = { ...detail, room };
            detailIdx++;
          }
        }
        
        setSlotPrices(newPrices);
        setEstimatedPrice(response.data);
        if (!isCustomPrice) {
          setCustomPriceValue(response.data.total?.toString() || '0');
        }
      } catch (error) {
        setSlotPrices({});
      }
    };
    
    fetchSlotPrices();
  }, [multiSlotMode, slots, formData.client_id, clients, rooms]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Multi-slot total
  const multiSlotTotal = useMemo(() => {
    if (!multiSlotMode || !estimatedPrice) return null;
    return {
      total: estimatedPrice.total || 0,
      totalHalfDays: estimatedPrice.total_half_days || 0,
      isConvention: estimatedPrice.is_convention || false,
      allFree: estimatedPrice.total === 0,
      hasPrice: (estimatedPrice.total || 0) > 0
    };
  }, [multiSlotMode, estimatedPrice]);

  const handleOpenDialog = async (reservation = null) => {
    if (reservation) {
      setSelectedReservation(reservation);
      // Convert UTC-stored datetimes to the user's local timezone (Europe/Paris in production)
      // so the datetime-local input shows the hour the user originally typed.
      const isoToLocalInput = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso.slice(0, 16);
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      };

      // If the reservation belongs to a multi-slot group, load ALL slots from the group
      let isMulti = false;
      let groupReservations = [reservation];
      if (reservation.group_id) {
        try {
          const allRes = await api.get('/reservations');
          const inGroup = (allRes.data || []).filter(r => r.group_id === reservation.group_id);
          if (inGroup.length > 1) {
            isMulti = true;
            groupReservations = inGroup.sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
          }
        } catch (_) { /* fallback to single-slot edit */ }
      }

      setMultiSlotMode(isMulti);
      if (isMulti) {
        // Multi-slot mode: keep the title/client/notes from any slot (they share the same)
        const first = groupReservations[0];
        setFormData({
          room_id: '',
          client_id: first.client_id,
          title: first.title,
          start_datetime: '',
          end_datetime: '',
          description: first.description || '',
          attendees_count: first.attendees_count?.toString() || '',
          notes: first.notes || '',
          special_needs: first.special_needs || '',
          services: first.services || [],
          event_contacts: toEventContactsForm(first.event_contacts)
        });
        setSelectedServices(first.services || []);
        setSlots(groupReservations.map(r => ({
          room_id: r.room_id,
          start_datetime: isoToLocalInput(r.start_datetime),
          end_datetime: isoToLocalInput(r.end_datetime),
          _reservation_id: r.id,
        })));
      } else {
        setFormData({
          room_id: reservation.room_id,
          client_id: reservation.client_id,
          title: reservation.title,
          start_datetime: isoToLocalInput(reservation.start_datetime),
          end_datetime: isoToLocalInput(reservation.end_datetime),
          description: reservation.description || '',
          attendees_count: reservation.attendees_count?.toString() || '',
          notes: reservation.notes || '',
          special_needs: reservation.special_needs || '',
          services: reservation.services || [],
          event_contacts: toEventContactsForm(reservation.event_contacts)
        });
        setSelectedServices(reservation.services || []);
      }

      // Pre-fill custom price/discount from the linked quote if any
      setIsCustomPrice(false);
      setCustomPriceType('amount');
      setCustomPriceValue('');
      if (reservation.quote_id) {
        try {
          const q = await api.get(`/quotes/${reservation.quote_id}`);
          if (q.data?.is_custom_price) {
            setIsCustomPrice(true);
            if (q.data.discount_percentage !== null && q.data.discount_percentage !== undefined) {
              setCustomPriceType('percentage');
              setCustomPriceValue(q.data.discount_percentage.toString());
            } else {
              setCustomPriceType('amount');
              setCustomPriceValue(q.data.amount?.toString() || '');
            }
          }
        } catch (_) { /* quote may have been deleted, leave defaults */ }
      }
    } else {
      setSelectedReservation(null);
      const clientId = (user?.role === 'utilisateur' && userClient) ? userClient.id : '';
      setFormData({
        room_id: '',
        client_id: clientId,
        title: '',
        start_datetime: '',
        end_datetime: '',
        description: '',
        attendees_count: '',
        notes: '',
        special_needs: '',
        services: [],
        event_contacts: EMPTY_EVENT_CONTACTS
      });
      setSelectedServices([]);
      setIsCustomPrice(false);
      setCustomPriceType('amount');
      setCustomPriceValue('');
    }
    setDialogOpen(true);
  };

  // When userClient is loaded, update formData if it's a new reservation
  useEffect(() => {
    if (userClient && user?.role === 'utilisateur' && !selectedReservation) {
      setFormData(prev => ({ ...prev, client_id: userClient.id }));
    }
  }, [userClient, user?.role, selectedReservation]);

  // Multi-slot functions
  const addSlot = () => {
    const lastSlot = slots[slots.length - 1];
    setSlots([...slots, { 
      room_id: lastSlot.room_id, 
      start_datetime: '', 
      end_datetime: '' 
    }]);
  };

  const removeSlot = (index) => {
    if (slots.length > 1) {
      setSlots(slots.filter((_, i) => i !== index));
    }
  };

  const updateSlot = (index, field, value) => {
    const newSlots = [...slots];
    newSlots[index][field] = value;
    setSlots(newSlots);
  };

  const duplicateSlotNextDay = (index) => {
    const slot = slots[index];
    if (slot.start_datetime && slot.end_datetime) {
      const startDate = new Date(slot.start_datetime);
      const endDate = new Date(slot.end_datetime);
      const newStart = addDays(startDate, 1);
      const newEnd = addDays(endDate, 1);
      
      // Format as local datetime for datetime-local input (YYYY-MM-DDTHH:mm)
      const formatLocalDatetime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };
      
      setSlots([...slots, {
        room_id: slot.room_id,
        start_datetime: formatLocalDatetime(newStart),
        end_datetime: formatLocalDatetime(newEnd)
      }]);
      toast.success('Créneau dupliqué au jour suivant');
    }
  };

  // Duplicate slot for same time but different room
  const duplicateSlotSameTime = (index) => {
    const slot = slots[index];
    if (slot.start_datetime && slot.end_datetime) {
      setSlots([...slots, {
        room_id: '', // Empty to let user choose another room
        start_datetime: slot.start_datetime,
        end_datetime: slot.end_datetime
      }]);
      toast.success('Créneau dupliqué - Sélectionnez une autre salle');
    }
  };

  const validateForm = () => {
    if (multiSlotMode) {
      // Validate multi-slot mode
      if (!formData.client_id) {
        toast.error('Veuillez sélectionner un bénéficiaire');
        return false;
      }
      if (!formData.title) {
        toast.error('Veuillez saisir un titre');
        return false;
      }
      if (!selectedReservation && !formData.event_contacts?.responsable_id) {
        toast.error("Veuillez désigner le responsable de l'événement");
        return false;
      }
      
      for (let i = 0; i < slots.length; i++) {
        const slot = slots[i];
        if (!slot.room_id) {
          toast.error(`Créneau ${i + 1}: Veuillez sélectionner une salle`);
          return false;
        }
        if (!slot.start_datetime || !slot.end_datetime) {
          toast.error(`Créneau ${i + 1}: Veuillez saisir les dates`);
          return false;
        }
        const startDate = new Date(slot.start_datetime);
        const endDate = new Date(slot.end_datetime);
        if (isBefore(endDate, startDate)) {
          toast.error(`Créneau ${i + 1}: La date de fin ne peut pas être antérieure à la date de début`);
          return false;
        }
        // Check if any slot is in the past (warning, not blocking)
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        if (startDate < now) {
          setPastDateWarning(true);
        }
      }
      return true;
    }
    
    // Single slot validation
    if (!formData.room_id) {
      toast.error('Veuillez sélectionner une salle');
      return false;
    }
    if (!formData.client_id) {
      toast.error('Veuillez sélectionner un bénéficiaire');
      return false;
    }
    if (!formData.title) {
      toast.error('Veuillez saisir un titre');
      return false;
    }
    if (!selectedReservation && !formData.event_contacts?.responsable_id) {
      toast.error("Veuillez désigner le responsable de l'événement");
      return false;
    }
    if (!formData.start_datetime || !formData.end_datetime) {
      toast.error('Veuillez saisir les dates de début et fin');
      return false;
    }
    
    const startDate = new Date(formData.start_datetime);
    const endDate = new Date(formData.end_datetime);
    
    if (isBefore(endDate, startDate)) {
      toast.error('La date de fin ne peut pas être antérieure à la date de début');
      return false;
    }
    
    if (startDate.getTime() === endDate.getTime()) {
      toast.error('La date de fin doit être différente de la date de début');
      return false;
    }
    
    // Check if date is in the past (warning, not blocking)
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Start of today
    if (startDate < now) {
      setPastDateWarning(true);
    } else {
      setPastDateWarning(false);
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    // Build the pricing query string used by both single and multi-slot endpoints.
    // Three options (mutually exclusive):
    //   - Custom amount  : ?is_custom_price=true&custom_price=99.99
    //   - Discount %     : ?is_custom_price=true&discount_percentage=20
    //   - None           : ?is_custom_price=false (only when editing, to clear)
    const buildPricingParams = (isEdit) => {
      if (isCustomPrice && customPriceValue !== '' && !isNaN(parseFloat(customPriceValue))) {
        if (customPriceType === 'percentage') {
          return `?is_custom_price=true&discount_percentage=${parseFloat(customPriceValue)}`;
        }
        return `?is_custom_price=true&custom_price=${parseFloat(customPriceValue)}`;
      }
      return isEdit ? `?is_custom_price=false` : '';
    };

    try {
      if (multiSlotMode && !selectedReservation) {
        const multiSlotData = {
          client_id: formData.client_id,
          title: formData.title,
          description: formData.description || '',
          attendees_count: parseInt(formData.attendees_count) || 0,
          notes: formData.notes || '',
          special_needs: formData.special_needs || '',
          services: selectedServices,
          event_contacts: toEventContactsPayload(formData.event_contacts),
          slots: slots.map(slot => ({
            room_id: slot.room_id,
            start_datetime: new Date(slot.start_datetime).toISOString(),
            end_datetime: new Date(slot.end_datetime).toISOString()
          }))
        };
        
        const url = `/reservations/multi-slot${buildPricingParams(false)}`;
        const response = await api.post(url, multiSlotData);
        
        toast.success(
          `${response.data.reservations.length} réservation(s) créée(s) avec devis unique N°${response.data.quote_number}`,
          { duration: 5000 }
        );
        
        setDialogOpen(false);
        setMultiSlotMode(false);
        setSlots([{ room_id: '', start_datetime: '', end_datetime: '' }]);
        loadData();
      } else if (multiSlotMode && selectedReservation) {
        // Multi-slot EDIT mode: update each slot individually.
        // Shared fields (title, client, description, notes, special_needs, services, attendees) propagate to ALL slots.
        // Pricing params are applied on the LAST request so the quote gets re-aggregated once.
        const sharedFields = {
          client_id: formData.client_id,
          title: formData.title,
          description: formData.description || '',
          attendees_count: parseInt(formData.attendees_count) || 0,
          notes: formData.notes || '',
          special_needs: formData.special_needs || '',
          services: selectedServices,
          event_contacts: toEventContactsPayload(formData.event_contacts),
        };
        const slotsWithId = slots.filter(s => s._reservation_id);
        for (let i = 0; i < slotsWithId.length; i++) {
          const slot = slotsWithId[i];
          const isLast = i === slotsWithId.length - 1;
          const url = `/reservations/${slot._reservation_id}` + (isLast ? buildPricingParams(true) : '');
          const body = {
            ...sharedFields,
            room_id: slot.room_id,
            start_datetime: new Date(slot.start_datetime).toISOString(),
            end_datetime: new Date(slot.end_datetime).toISOString(),
          };
          await api.put(url, body);
        }
        toast.success(`${slotsWithId.length} créneau(x) mis à jour`);
        setDialogOpen(false);
        setMultiSlotMode(false);
        setPastDateWarning(false);
        loadData();
      } else {
        // Single reservation mode
        const data = {
          ...formData,
          start_datetime: new Date(formData.start_datetime).toISOString(),
          end_datetime: new Date(formData.end_datetime).toISOString(),
          attendees_count: parseInt(formData.attendees_count) || 0,
          services: selectedServices,
          event_contacts: toEventContactsPayload(formData.event_contacts)
        };
        
        // Add custom price info (applies to both create and edit)
        const url = (selectedReservation ? `/reservations/${selectedReservation.id}` : '/reservations')
          + buildPricingParams(!!selectedReservation);

        if (selectedReservation) {
          await api.put(url, data);
          toast.success('Réservation mise à jour');
        } else {
          await api.post(url, data);
          toast.success('Réservation créée avec devis automatique');
        }
        setDialogOpen(false);
        setPastDateWarning(false);
        loadData();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la sauvegarde';
      // Check if this is a conflict error (room already booked)
      if (errorMessage.includes('conflit') || errorMessage.includes('déjà réservée') || errorMessage.includes('already booked')) {
        setConflictMessage(errorMessage);
        setConflictModalOpen(true);
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleStatusChange = async (reservation, newStatus) => {
    try {
      await api.put(`/reservations/${reservation.id}`, { status: newStatus });
      toast.success('Statut mis à jour');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour du statut');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/reservations/${selectedReservation.id}`);
      toast.success('Réservation supprimée');
      setDeleteDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <Layout>
      <PageHeader 
        title="Réservations" 
        description={isUser ? "Vos réservations de salles" : "Gérez toutes les réservations de salles"}
        actions={
          <Button 
            onClick={() => handleOpenDialog()}
            className="bg-[#1e3a8a] hover:bg-[#1e40af] btn-animate"
            data-testid="add-reservation-button"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nouvelle réservation
          </Button>
        }
      />

      <ReservationTable
        reservations={reservations}
        filteredReservations={filteredReservations}
        paginatedReservations={paginatedReservations}
        rooms={rooms}
        loading={loading}
        search={search}
        setSearch={setSearch}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        roomFilter={roomFilter}
        setRoomFilter={setRoomFilter}
        pageSize={pageSize}
        setPageSize={setPageSize}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        isUser={isUser}
        isAdmin={isAdmin}
        isGestionnaire={isGestionnaire}
        onEdit={handleOpenDialog}
        onDelete={(res) => { setSelectedReservation(res); setDeleteDialogOpen(true); }}
        onStatusChange={handleStatusChange}
        onNew={() => handleOpenDialog()}
      />

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) {
          setMultiSlotMode(false);
          setSlots([{ room_id: '', start_datetime: '', end_datetime: '' }]);
        }
      }}>
        <DialogContent className={multiSlotMode ? "max-w-2xl max-h-[90vh] overflow-y-auto" : "max-w-lg max-h-[90vh] overflow-y-auto"}>
          <DialogHeader>
            <DialogTitle className="text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>
              {selectedReservation ? 'Modifier la réservation' : 'Nouvelle réservation'}
            </DialogTitle>
            {!selectedReservation && !isUser && (
              <div className="flex items-center gap-2 pt-2">
                <Button
                  type="button"
                  variant={multiSlotMode ? "outline" : "default"}
                  size="sm"
                  onClick={() => setMultiSlotMode(false)}
                  className={!multiSlotMode ? "bg-[#1e3a8a] hover:bg-[#1e40af]" : ""}
                >
                  Créneau unique
                </Button>
                <Button
                  type="button"
                  variant={multiSlotMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setMultiSlotMode(true);
                    // Copy room_id from formData to first slot if set
                    if (formData.room_id) {
                      setSlots([{ room_id: formData.room_id, start_datetime: formData.start_datetime || '', end_datetime: formData.end_datetime || '' }]);
                    }
                  }}
                  className={multiSlotMode ? "bg-[#1e3a8a] hover:bg-[#1e40af]" : ""}
                >
                  Multi-créneaux
                </Button>
              </div>
            )}
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="Réunion paroissiale"
                data-testid="reservation-title-input"
              />
            </div>

            {/* Beneficiary selection - same for both modes */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Bénéficiaire / Organisateur *</Label>
                {!isUser && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setNewClientDialogOpen(true)}
                    className="text-[#2563EB] hover:text-[#1e3a8a] h-auto py-1 px-2"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Nouveau
                  </Button>
                )}
              </div>
              {isUser && userClient ? (
                <Input
                  value={userClient.name}
                  disabled
                  className="bg-slate-50"
                  data-testid="reservation-client-display"
                />
              ) : (
                <Popover open={clientSearchOpen} onOpenChange={setClientSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={clientSearchOpen}
                      className="w-full justify-between font-normal"
                      data-testid="reservation-client-select"
                    >
                      {selectedClient ? selectedClient.name : "Rechercher un bénéficiaire..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0" align="start">
                    <Command>
                      <CommandInput 
                        placeholder="Rechercher un bénéficiaire..." 
                        value={clientSearch}
                        onValueChange={setClientSearch}
                      />
                      <CommandList>
                        <CommandEmpty>Aucun bénéficiaire trouvé.</CommandEmpty>
                        <CommandGroup>
                          {filteredClients.map((client) => (
                            <CommandItem
                              key={client.id}
                              value={client.name}
                              onSelect={() => {
                                setFormData({
                                  ...formData,
                                  client_id: client.id,
                                  event_contacts: client.id === formData.client_id ? formData.event_contacts : EMPTY_EVENT_CONTACTS,
                                });
                                setClientSearchOpen(false);
                                setClientSearch('');
                              }}
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${formData.client_id === client.id ? "opacity-100" : "opacity-0"}`}
                              />
                              <div>
                                <p className="font-medium">{client.name}</p>
                                <p className="text-xs text-slate-500">{client.contact?.email}</p>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            </div>

            {selectedClient && (
              <EventContactsPicker
                client={selectedClient}
                value={formData.event_contacts || EMPTY_EVENT_CONTACTS}
                onChange={(ec) => setFormData((prev) => ({ ...prev, event_contacts: ec }))}
                onContactCreated={handleEventContactCreated}
              />
            )}

            {/* Single slot mode */}
            {!multiSlotMode && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Salle *</Label>
                    <Select 
                      value={formData.room_id} 
                      onValueChange={(value) => setFormData({ ...formData, room_id: value })}
                    >
                      <SelectTrigger data-testid="reservation-room-select">
                        <SelectValue placeholder="Sélectionner une salle..." />
                      </SelectTrigger>
                      <SelectContent>
                        {rooms.map(room => (
                          <SelectItem key={room.id} value={room.id}>
                            {room.name} ({room.capacity} pers.)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Nombre de participants</Label>
                    <Input
                      type="number"
                      value={formData.attendees_count}
                      onChange={(e) => setFormData({ ...formData, attendees_count: e.target.value })}
                      min="0"
                      placeholder="25"
                      data-testid="reservation-attendees-input"
                    />
                  </div>
                </div>

                {/* Room info summary with capacities and pricing */}
                {formData.room_id && (
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
                    {/* Room capacities */}
                    {(() => {
                      const selectedRoom = rooms.find(r => r.id === formData.room_id);
                      if (!selectedRoom) return null;
                      return (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-slate-700">Capacités de la salle</p>
                          </div>
                          <div className="flex items-center gap-3 text-sm">
                            <span className="flex items-center gap-1">
                              <span className="font-medium text-slate-800">{selectedRoom.capacity}</span>
                              <span className="text-slate-500">assis</span>
                            </span>
                            {selectedRoom.capacity_seated_table && (
                              <span className="flex items-center gap-1 text-slate-600">
                                <span className="text-slate-300">|</span>
                                <span className="font-medium">{selectedRoom.capacity_seated_table}</span>
                                <span className="text-slate-500">à table</span>
                              </span>
                            )}
                            {selectedRoom.capacity_standing && (
                              <span className="flex items-center gap-1 text-slate-600">
                                <span className="text-slate-300">|</span>
                                <span className="font-medium">{selectedRoom.capacity_standing}</span>
                                <span className="text-slate-500">debout</span>
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                    
                    {/* Pricing info - half-day system */}
                    {formData.client_id && (
                      <div className="pt-2 border-t border-slate-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-blue-800">Participation estimée</p>
                            <p className="text-xs text-blue-600">
                              {selectedClient && `Type: ${selectedClient.client_type}`}
                              {selectedClient?.convention && ' (Convention)'}
                            </p>
                          </div>
                          {priceLoading ? (
                            <div className="spinner" style={{ width: 20, height: 20 }} />
                          ) : estimatedPrice ? (
                            <div className="text-right">
                              {estimatedPrice.is_convention ? (
                                <span className="text-lg font-bold text-emerald-600">Gratuit (Convention)</span>
                              ) : estimatedPrice.total === 0 ? (
                                <span className="text-lg font-bold text-emerald-600">Gratuit</span>
                              ) : (
                                <div>
                                  <span className="text-lg font-bold text-blue-800">
                                    {estimatedPrice.total?.toFixed(2)} €
                                  </span>
                                  <span className="text-xs text-slate-500 ml-1">
                                    ({estimatedPrice.total_half_days} demi-journée{estimatedPrice.total_half_days > 1 ? 's' : ''})
                                  </span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">Sélectionnez dates pour estimer</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Début *</Label>
                    <Input
                      type="datetime-local"
                      value={formData.start_datetime}
                      onChange={(e) => {
                        const newStart = e.target.value;
                        setFormData({ 
                          ...formData, 
                          start_datetime: newStart,
                          end_datetime: formData.end_datetime && new Date(formData.end_datetime) < new Date(newStart) 
                            ? newStart 
                            : formData.end_datetime
                        });
                        // Check if date is in the past
                        if (newStart) {
                          const startDate = new Date(newStart);
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          setPastDateWarning(startDate < today);
                        }
                      }}
                      required
                      data-testid="reservation-start-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Fin *</Label>
                    <Input
                      type="datetime-local"
                      value={formData.end_datetime}
                      onChange={(e) => setFormData({ ...formData, end_datetime: e.target.value })}
                      min={formData.start_datetime}
                      required
                      data-testid="reservation-end-input"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Multi-slot mode */}
            {multiSlotMode && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Créneaux de réservation</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addSlot}
                    className="text-[#2563EB]"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter un créneau
                  </Button>
                </div>
                
                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                  {slots.map((slot, index) => (
                    <div key={`slot-${index}-${slot.room_id}`} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-600">Créneau {index + 1}</span>
                        <div className="flex items-center gap-1">
                          {slot.start_datetime && slot.end_datetime && (
                            <>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => duplicateSlotNextDay(index)}
                                title="Dupliquer au lendemain (même salle, J+1)"
                                className="h-7 px-2 text-blue-600 hover:text-blue-800"
                              >
                                <Copy className="h-3 w-3 mr-1" />
                                J+1
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => duplicateSlotSameTime(index)}
                                title="Dupliquer aux mêmes heures (autre salle)"
                                className="h-7 px-2 text-emerald-600 hover:text-emerald-800"
                              >
                                <Copy className="h-3 w-3 mr-1" />
                                Autre salle
                              </Button>
                            </>
                          )}
                          {slots.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeSlot(index)}
                              className="h-7 px-2 text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Salle</Label>
                          <Select 
                            value={slot.room_id} 
                            onValueChange={(value) => updateSlot(index, 'room_id', value)}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Salle..." />
                            </SelectTrigger>
                            <SelectContent>
                              {rooms.map(room => (
                                <SelectItem key={room.id} value={room.id}>
                                  {room.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-1">
                          <Label className="text-xs">Début</Label>
                          <Input
                            type="datetime-local"
                            value={slot.start_datetime}
                            onChange={(e) => updateSlot(index, 'start_datetime', e.target.value)}
                            className="h-9 text-sm"
                          />
                        </div>
                        
                        <div className="space-y-1">
                          <Label className="text-xs">Fin</Label>
                          <Input
                            type="datetime-local"
                            value={slot.end_datetime}
                            onChange={(e) => updateSlot(index, 'end_datetime', e.target.value)}
                            min={slot.start_datetime}
                            className="h-9 text-sm"
                          />
                        </div>
                      </div>
                      
                      {/* Room info summary for this slot */}
                      {slot.room_id && slotPrices[index] && (
                        <div className="mt-2 p-2 bg-white rounded border border-slate-200 text-xs">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-slate-600">
                              <span className="font-medium">{slotPrices[index].room?.capacity || '-'}</span>
                              <span className="text-slate-400">assis</span>
                              {slotPrices[index].room?.capacity_seated_table && (
                                <>
                                  <span className="text-slate-300">|</span>
                                  <span className="font-medium">{slotPrices[index].room.capacity_seated_table}</span>
                                  <span className="text-slate-400">à table</span>
                                </>
                              )}
                              {slotPrices[index].room?.capacity_standing && (
                                <>
                                  <span className="text-slate-300">|</span>
                                  <span className="font-medium">{slotPrices[index].room.capacity_standing}</span>
                                  <span className="text-slate-400">debout</span>
                                </>
                              )}
                            </div>
                            <div className="text-right">
                              {slotPrices[index].subtotal === 0 ? (
                                <span className="font-semibold text-emerald-600">Gratuit</span>
                              ) : (
                                <span className="font-semibold text-blue-700">
                                  {(slotPrices[index].subtotal || 0).toFixed(2)} € ({slotPrices[index].half_days || 1} DJ)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Multi-slot summary with total */}
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
                  <p className="text-sm text-slate-700">
                    <strong>{slots.length}</strong> créneau(x) seront créés comme réservations séparées avec le même titre et bénéficiaire.
                  </p>
                  
                  {formData.client_id && Object.keys(slotPrices).length > 0 && (
                    <div className="pt-2 border-t border-slate-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-600">Total participation estimée :</span>
                        {multiSlotTotal?.isConvention ? (
                          <span className="text-lg font-bold text-emerald-600">Gratuit (Convention)</span>
                        ) : multiSlotTotal?.allFree ? (
                          <span className="text-lg font-bold text-emerald-600">Gratuit</span>
                        ) : multiSlotTotal?.hasPrice ? (
                          <span className="text-lg font-bold text-blue-800">
                            {multiSlotTotal.total.toFixed(2)} € ({multiSlotTotal.totalHalfDays} demi-journées)
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Règle : 1ère DJ = 100% | 2ème = -25% | 3ème+ = -50%
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Détails de l'événement..."
                rows={2}
                data-testid="reservation-description-input"
              />
            </div>

            {!isUser && (
              <div className="space-y-2">
                <Label>Notes internes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notes pour le personnel..."
                  rows={2}
                  data-testid="reservation-notes-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Besoins particuliers</Label>
              <Textarea
                value={formData.special_needs}
                onChange={(e) => setFormData({ ...formData, special_needs: e.target.value })}
                placeholder="Ex: cafetière, bouilloire, tables supplémentaires, vidéoprojecteur..."
                rows={2}
                data-testid="reservation-special-needs-input"
              />
              <p className="text-xs text-slate-500">Ces informations apparaîtront sur le planning et les documents</p>
            </div>

            {/* Services inclus */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Services inclus</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'bouilloire', label: 'Bouilloire' },
                  { id: 'machine_cafe', label: 'Machine à café' },
                  { id: 'video_projection', label: 'Vidéo-projection' },
                  { id: 'visio_conference', label: 'Visio-conférence' },
                  { id: 'parking', label: 'Parking' },
                  { id: 'menage', label: 'Ménage' },
                ].map(service => (
                  <label key={service.id} className="flex items-center gap-2 p-2 rounded border border-slate-200 hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedServices.includes(service.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedServices(prev => [...prev, service.id]);
                        } else {
                          setSelectedServices(prev => prev.filter(s => s !== service.id));
                        }
                      }}
                      className="h-4 w-4 rounded border-gray-300"
                      data-testid={`service-${service.id}`}
                    />
                    <span className="text-sm text-slate-700">{service.label}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500">Équipement café et restauration en autonomie inclus dans le prix</p>
            </div>

            {/* Price Summary */}
            {estimatedPrice && !multiSlotMode && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2" data-testid="price-summary">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-blue-800">Estimation tarifaire</span>
                  {estimatedPrice.is_convention && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-semibold">CONVENTION</span>
                  )}
                </div>
                {estimatedPrice.slot_details?.[0] && (
                  <div className="text-xs text-blue-700">
                    {estimatedPrice.slot_details[0].duration_hours}h → {estimatedPrice.slot_details[0].half_days} demi-journée(s) × {estimatedPrice.slot_details[0].half_day_rate}€
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-blue-700">Tarif standard :</span>
                  <span className="font-bold text-blue-900">{estimatedPrice.is_convention ? 'Gratuit (Convention)' : `${(estimatedPrice.original_total ?? estimatedPrice.total).toFixed(2)} €`}</span>
                  {!estimatedPrice.is_convention && estimatedPrice.original_total != null && estimatedPrice.original_total !== estimatedPrice.total && (
                    <>
                      <span className="text-blue-400">→</span>
                      <span className="font-bold text-amber-700 text-lg">{estimatedPrice.total.toFixed(2)} €</span>
                      {estimatedPrice.discount_percentage != null && (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-semibold">−{estimatedPrice.discount_percentage}%</span>
                      )}
                    </>
                  )}
                </div>
                {!estimatedPrice.is_convention && (
                  <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-blue-200">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isCustomPrice}
                        onChange={(e) => {
                          setIsCustomPrice(e.target.checked);
                          if (!e.target.checked) setCustomPriceValue('');
                        }}
                        className="h-4 w-4 rounded"
                        data-testid="custom-price-toggle"
                      />
                      <span className="text-xs text-blue-700 font-medium">Tarif exceptionnel</span>
                    </label>
                    {isCustomPrice && (
                      <>
                        <select
                          value={customPriceType}
                          onChange={(e) => { setCustomPriceType(e.target.value); setCustomPriceValue(''); }}
                          className="h-7 text-xs border border-blue-300 rounded px-1"
                          data-testid="custom-price-type-select"
                        >
                          <option value="amount">Montant fixe (€)</option>
                          <option value="percentage">Remise en %</option>
                        </select>
                        <input
                          type="number"
                          value={customPriceValue}
                          onChange={(e) => setCustomPriceValue(e.target.value)}
                          className="w-24 h-7 text-sm border border-blue-300 rounded px-2 text-right"
                          min="0"
                          max={customPriceType === 'percentage' ? 100 : undefined}
                          step={customPriceType === 'percentage' ? '1' : '0.01'}
                          placeholder={customPriceType === 'percentage' ? 'ex: 20' : 'ex: 99.99'}
                          data-testid="custom-price-input"
                        />
                        <span className="text-sm text-blue-700">{customPriceType === 'percentage' ? '%' : '€'}</span>
                        <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-semibold">TARIF EXCEPTIONNEL</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Multi-slot total */}
            {multiSlotMode && estimatedPrice && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2" data-testid="multi-slot-total">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-blue-800">Total estimation ({estimatedPrice.total_half_days} demi-journées)</span>
                  {estimatedPrice.is_convention && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-semibold">CONVENTION</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-blue-900 text-lg">
                    {estimatedPrice.is_convention ? 'Gratuit (Convention)' : `${(estimatedPrice.original_total ?? estimatedPrice.total).toFixed(2)} €`}
                  </span>
                  {!estimatedPrice.is_convention && estimatedPrice.original_total != null && estimatedPrice.original_total !== estimatedPrice.total && (
                    <>
                      <span className="text-blue-400">→</span>
                      <span className="font-bold text-amber-700 text-lg">{estimatedPrice.total.toFixed(2)} €</span>
                      {estimatedPrice.discount_percentage != null && (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-semibold">−{estimatedPrice.discount_percentage}%</span>
                      )}
                    </>
                  )}
                </div>
                {!estimatedPrice.is_convention && (
                  <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-blue-200">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={isCustomPrice} onChange={(e) => { setIsCustomPrice(e.target.checked); if (!e.target.checked) setCustomPriceValue(''); }} className="h-4 w-4 rounded" data-testid="multi-custom-price-toggle" />
                      <span className="text-xs text-blue-700 font-medium">Tarif exceptionnel</span>
                    </label>
                    {isCustomPrice && (
                      <>
                        <select value={customPriceType} onChange={(e) => { setCustomPriceType(e.target.value); setCustomPriceValue(''); }} className="h-7 text-xs border border-blue-300 rounded px-1" data-testid="multi-custom-price-type-select">
                          <option value="amount">Montant fixe (€)</option>
                          <option value="percentage">Remise en %</option>
                        </select>
                        <input type="number" value={customPriceValue} onChange={(e) => setCustomPriceValue(e.target.value)} className="w-24 h-7 text-sm border border-blue-300 rounded px-2 text-right" min="0" max={customPriceType === 'percentage' ? 100 : undefined} step={customPriceType === 'percentage' ? '1' : '0.01'} placeholder={customPriceType === 'percentage' ? 'ex: 20' : 'ex: 99.99'} data-testid="multi-custom-price-input" />
                        <span className="text-sm text-blue-700">{customPriceType === 'percentage' ? '%' : '€'}</span>
                        <span className="ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-semibold">TARIF EXCEPTIONNEL</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Past date warning */}
            {pastDateWarning && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    Attention : Date dans le passé
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    La date de début sélectionnée est antérieure à aujourd'hui. 
                    Si c'est une régularisation, vous pouvez continuer. Sinon, vérifiez la date.
                  </p>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setPastDateWarning(false); }}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="save-reservation-button">
                {selectedReservation ? 'Mettre à jour' : multiSlotMode ? `Créer ${slots.length} réservation(s)` : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette réservation ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. La réservation "{selectedReservation?.title}" sera définitivement supprimée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-reservation"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NewClientDialog
        open={newClientDialogOpen}
        onOpenChange={setNewClientDialogOpen}
        clientData={newClientData}
        setClientData={setNewClientData}
        onSave={handleCreateNewClient}
        saving={creatingClient}
      />

      {/* Conflict Modal - Large centered alert for duplicate bookings */}
      <Dialog open={conflictModalOpen} onOpenChange={setConflictModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-red-600 text-xl flex items-center gap-2">
              <AlertTriangle className="h-6 w-6" />
              Conflit de réservation
            </DialogTitle>
          </DialogHeader>
          <div className="py-6">
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6 text-center">
              <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-lg font-medium text-red-800 mb-2">
                Cette salle est déjà réservée !
              </p>
              <p className="text-red-700">
                {conflictMessage}
              </p>
            </div>
            <p className="text-sm text-slate-500 mt-4 text-center">
              Veuillez choisir un autre créneau horaire ou une autre salle.
            </p>
          </div>
          <DialogFooter className="justify-center">
            <Button 
              onClick={() => setConflictModalOpen(false)}
              className="bg-red-600 hover:bg-red-700 px-8"
              data-testid="close-conflict-modal"
            >
              <X className="h-4 w-4 mr-2" />
              J'ai compris
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
